import React, { useEffect, useState, useRef } from 'react';
// eslint-disable-next-line import/no-extraneous-dependencies
import {
  CompanySearch,
  CompanyMap,
} from '@boligmappa/company-search-component';
// eslint-disable-next-line import/no-extraneous-dependencies, import/extensions
import '@boligmappa/company-search-component/styles';
import { searchApiBaseUrl, companyDetailsBaseUrl } from '../../config';
import { getValidSearchApiToken } from '../../api/searchApiAuth';

// Map CSS imports - loaded conditionally when showMap is true
let mapStylesImported = false;

// Track fetch interceptor state to handle multiple component instances
let fetchInterceptorInstalled = false;
let originalFetch = null;
let componentCount = 0;

/**
 * Wrapper component for the company-search-component that integrates
 * with the existing Search API token authentication system.
 *
 * This component intercepts fetch calls to add the Authorization header
 * with a valid Search API token. All coordination between search and map
 * should be handled by the parent component.
 */
const CompanySearchWrapper = ({
  onResultsChange,
  onResultClick,
  onCompanyClick,
  onOpenMapPopup,
  onDetailViewClose,
  renderCompanyCard,
  labels,
  pageSize = 15,
  showMap = false,
  companyDetailsApiBaseUrl,
  fontClassName,
  companySearchRef: externalCompanySearchRef,
  companyMapRef: externalCompanyMapRef,
  searchResults: externalSearchResults,
  ...otherProps
}) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const internalCompanySearchRef = useRef(null);
  const internalCompanyMapRef = useRef(null);
  const mapContainerRef = useRef(null);
  const [internalSearchResults, setInternalSearchResults] = useState([]);

  // Use external refs if provided, otherwise use internal ones
  const companySearchRef = externalCompanySearchRef || internalCompanySearchRef;
  const companyMapRef = externalCompanyMapRef || internalCompanyMapRef;

  // Use external searchResults if provided, otherwise use internal state
  const searchResults = externalSearchResults ?? internalSearchResults;

  useEffect(() => {
    // Verify authentication is working
    const verifyAuth = async () => {
      const [tokenError, token] = await getValidSearchApiToken();
      if (!tokenError && token) {
        setIsAuthenticated(true);
      }
    };

    verifyAuth();

    // Install fetch interceptor (only once, shared across all instances)
    if (!fetchInterceptorInstalled) {
      originalFetch = window.fetch;

      const authenticatedFetch = async (url, options = {}) => {
        const urlString = typeof url === 'string' ? url : url.toString();

        // Only intercept requests to the Search API
        const isSearchApiRequest =
          urlString.includes(searchApiBaseUrl) ||
          urlString.includes('/api/v1/Companies') ||
          urlString.includes('/api/companies/search') ||
          urlString.includes('/api/v1/companies/externalprofile') ||
          urlString.includes(companyDetailsBaseUrl);

        if (isSearchApiRequest) {
          const [tokenError, token] = await getValidSearchApiToken();

          if (tokenError || !token) {
            // eslint-disable-next-line no-console
            console.error('Failed to obtain Search API token:', tokenError);
            return originalFetch(url, options);
          }

          const headers = {
            ...options.headers,
            Authorization: `Bearer ${token}`,
          };

          const response = await originalFetch(url, {
            ...options,
            headers,
          });

          // Handle 401 Unauthorized - token might be invalid
          if (response.status === 401) {
            // eslint-disable-next-line no-console
            console.warn('Search API token expired, refreshing...');
            const [refreshError, newToken] = await getValidSearchApiToken();

            if (!refreshError && newToken) {
              const retryHeaders = {
                ...options.headers,
                Authorization: `Bearer ${newToken}`,
              };

              return originalFetch(url, {
                ...options,
                headers: retryHeaders,
              });
            }
          }

          return response;
        }

        // For non-Search API requests, use original fetch
        return originalFetch(url, options);
      };

      window.fetch = authenticatedFetch;
      fetchInterceptorInstalled = true;
    }

    // Track component instances
    componentCount += 1;

    // Cleanup: restore original fetch only when last component unmounts
    return () => {
      componentCount -= 1;
      if (componentCount === 0 && fetchInterceptorInstalled && originalFetch) {
        window.fetch = originalFetch;
        fetchInterceptorInstalled = false;
        originalFetch = null;
      }
    };
  }, []);

  // Load map styles if map is enabled
  useEffect(() => {
    if (showMap && !mapStylesImported) {
      // Dynamically import map CSS when map is first shown
      // eslint-disable-next-line import/no-extraneous-dependencies
      import('leaflet/dist/leaflet.css');
      // eslint-disable-next-line import/no-extraneous-dependencies
      import('maplibre-gl/dist/maplibre-gl.css');
      mapStylesImported = true;
    }
  }, [showMap]);

  // Fix map size when it becomes visible after being hidden
  // This prevents NaN coordinate errors when the map container was hidden
  useEffect(() => {
    if (!showMap || !companyMapRef?.current) {
      return;
    }

    const invalidateMapSize = () => {
      // Small delay to ensure DOM has updated and container is visible
      setTimeout(() => {
        if (!companyMapRef?.current) {
          return;
        }

        // Check if the map container is actually visible
        const containerElement = mapContainerRef.current;
        if (containerElement) {
          const computedStyle = window.getComputedStyle(containerElement);
          const isVisible =
            computedStyle.display !== 'none' &&
            computedStyle.visibility !== 'hidden' &&
            containerElement.offsetWidth > 0 &&
            containerElement.offsetHeight > 0;

          if (!isVisible) {
            // Container is not visible yet, try again after a longer delay
            setTimeout(invalidateMapSize, 200);
            return;
          }
        }

        // Try to call invalidateSize if the method exists on the ref
        if (typeof companyMapRef.current.invalidateSize === 'function') {
          companyMapRef.current.invalidateSize();
        }
        // Try to access the underlying Leaflet map instance
        else if (
          typeof companyMapRef.current.getMap === 'function' &&
          companyMapRef.current.getMap()
        ) {
          const leafletMap = companyMapRef.current.getMap();
          if (leafletMap && typeof leafletMap.invalidateSize === 'function') {
            leafletMap.invalidateSize();
          }
        }
        // Try direct access to map property
        else if (
          companyMapRef.current.map &&
          typeof companyMapRef.current.map.invalidateSize === 'function'
        ) {
          companyMapRef.current.map.invalidateSize();
        }
      }, 100);
    };

    // Invalidate size when showMap becomes true
    invalidateMapSize();

    // Also handle window resize events
    const handleResize = () => {
      invalidateMapSize();
    };

    window.addEventListener('resize', handleResize);

    // eslint-disable-next-line consistent-return
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [showMap, companyMapRef]);

  // Simple handler that just passes results to parent
  // Parent can handle filtering, map coordination, etc.
  const handleResultsChange = (results) => {
    // If parent is managing searchResults, just pass through
    if (externalSearchResults !== undefined) {
      if (onResultsChange) {
        onResultsChange(results);
      }
      return;
    }

    // Otherwise, manage internal state and notify parent
    setInternalSearchResults(results);
    if (onResultsChange) {
      onResultsChange(results);
    }
  };

  // Construct the full API endpoint URL
  const searchApiEndpoint = `${searchApiBaseUrl}/api/v1/Companies/search`;
  const detailsApiBaseUrl = companyDetailsApiBaseUrl || companyDetailsBaseUrl;

  if (!isAuthenticated) {
    return (
      <div className="flex items-center justify-center p-8">
        <p className="text-gray-500">Initializing authentication...</p>
      </div>
    );
  }

  return (
    <div
      className="flex flex-col 3xl:flex-row gap-6 h-[calc(100vh-120px)] w-full max-w-screen-3xl mx-auto"
      {...otherProps}
    >
      <div className="flex-1 w-full 3xl:w-1/2 min-h-[500px] h-full flex flex-col">
        <div className="h-full w-full company-search-component company-search-wrapper flex flex-col">
          <CompanySearch
            ref={companySearchRef}
            apiEndpoint={searchApiEndpoint}
            companyDetailsApiBaseUrl={detailsApiBaseUrl}
            onResultClick={onResultClick}
            onResultsChange={handleResultsChange}
            onOpenMapPopup={onOpenMapPopup}
            onDetailViewClose={onDetailViewClose}
            renderCompanyCard={renderCompanyCard}
            labels={labels}
            pageSize={pageSize}
            fontClassName={fontClassName}
          />
        </div>
      </div>
      {showMap && (
        <div
          ref={mapContainerRef}
          className="hidden 3xl:flex flex-1 3xl:w-1/2 min-h-[500px] h-full relative z-0"
        >
          <CompanyMap
            ref={companyMapRef}
            companies={searchResults}
            companyDetailsApiBaseUrl={detailsApiBaseUrl}
            onCompanyClick={onCompanyClick}
            fontClassName={fontClassName}
          />
        </div>
      )}
    </div>
  );
};

export default CompanySearchWrapper;
